/**
 * AgentEvaluationWorkspacePanel — the EVALUATE/IMPROVE surface of MASTER_FLOW §15A
 * (FULL-0128 / PROD-OPS-042).
 *
 * §15A's three operator mental models are BUILD/CONFIGURE → MONITOR/OPERATE → EVALUATE/IMPROVE.
 * The first two have surfaces; the third did not. Four existing, admin/owner-gated read routes
 * already answer it and had no screen:
 *
 *     GET /api/agents/product-eval/contract                     the shipped suites, versions, digests
 *     GET /api/agents/product-eval/promotion/contract           the arm/gate/promotion contract
 *     GET /api/agents/product-eval/promotion/evidence           stored, digest-verified evidence
 *     GET /api/agents/product-eval/promotion/promotions         recorded human decisions
 *
 * This panel is their first consumer. It is READ-ONLY and mounts none of the recording, approval or
 * promotion routes: an operator can inspect what was evaluated, which ADOPT/WATCH/REJECT arm the
 * server derived, and whether a second human recorded a promotion — but cannot submit a candidate,
 * mint an approval or promote from here. Those remain the explicit operator path, and the server's
 * own authority block says a promotion changes no production default and no live routing.
 *
 * TRUTHFULNESS RULES (pinned by tests):
 *   - a source that is still loading, failed, or answered with no value is three different
 *     sentences; none is drawn as an empty list;
 *   - a key the payload did not carry is "not reported", never an empty result and never a zero;
 *   - an arm/gate/integrity/reason token this build does not know is named "לא מזוהה" and the raw
 *     token is not printed as a word;
 *   - a server authority flag is printed from the payload; an absent flag reads "לא דווח" rather
 *     than being assumed false.
 *
 * Nothing here renders a prompt, a recording body or a credential: the payload carries bounded
 * identifiers, counts and scores, and so does the screen.
 */
import {
  EVALUATION_NOT_REPORTED_HE,
  armLabel,
  armReasonLabel,
  eligibilityLabel,
  flagLabel,
  flagState,
  gateLabel,
  integrityLabel,
} from "./agentEvaluationVocabulary";

const SOURCE_LOADING = "loading";
const SOURCE_FAILED = "failed";
const SOURCE_READY = "ready";

function textOrNotReported(value) {
  if (value === null || value === undefined || value === "") return EVALUATION_NOT_REPORTED_HE;
  return String(value);
}

function countText(value) {
  return typeof value === "number" && Number.isFinite(value)
    ? String(value)
    : EVALUATION_NOT_REPORTED_HE;
}

function percentText(value) {
  return typeof value === "number" && Number.isFinite(value)
    ? `${Math.round(value * 100)}%`
    : EVALUATION_NOT_REPORTED_HE;
}

function dateText(value) {
  if (!value) return EVALUATION_NOT_REPORTED_HE;
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return "זמן לא ידוע";
  return new Intl.DateTimeFormat("he-IL", { dateStyle: "short", timeStyle: "short" }).format(parsed);
}

function SourceGate({ state, payload, testId, label, notReportedTestId, children }) {
  if (state === SOURCE_LOADING) {
    return (
      <div role="status" data-testid={`${testId}-loading`} className="flex justify-center py-6">
        <span className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-600" />
      </div>
    );
  }
  if (state === SOURCE_FAILED) {
    return (
      <p
        role="alert"
        data-testid={`${testId}-failed`}
        className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1"
      >
        {label} — לא ניתן לטעון את המקור. אין כאן הצגה חלקית של מצב לא ידוע.
      </p>
    );
  }
  if (payload === null || payload === undefined) {
    return (
      <p
        role="alert"
        data-testid={notReportedTestId}
        className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
      >
        המקור לא דיווח על {label} — לא ידוע, ולא רשימה ריקה.
      </p>
    );
  }
  return children(payload);
}

function Chip({ children, tone = "bg-gray-50 text-gray-600 border-gray-200" }) {
  return (
    <span className={`text-[11px] px-1.5 py-0.5 rounded-full border whitespace-nowrap ${tone}`}>
      {children}
    </span>
  );
}

function SectionShell({ title, description, children, testId }) {
  return (
    <section
      aria-label={title}
      data-testid={testId}
      className="bg-white rounded-xl border shadow-sm p-4 space-y-3"
    >
      <div>
        <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
        {description && <p className="text-[11px] text-gray-400 mt-0.5">{description}</p>}
      </div>
      {children}
    </section>
  );
}

// ─── Suites: what this deployment holds, and whether it is intact ─────────────

export function EvaluationSuitesSection({ contract }) {
  const suites = contract?.suites;
  if (!Array.isArray(suites)) {
    return (
      <p
        role="alert"
        data-testid="evaluation-suites-not-reported"
        className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
      >
        המקור לא דיווח על מערכי ההערכה (suites) — לא ידוע, ולא אפס.
      </p>
    );
  }

  if (suites.length === 0) {
    return (
      <p data-testid="evaluation-suites-empty" className="text-sm text-gray-500">
        אין מערכי הערכה משולחים בגרסה הזאת.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {suites.map((suite) => {
        const suiteId = suite?.suite_id;
        const digestMismatch = suite?.digest_matches === false;
        const digestUnknown = suite?.digest_matches !== true && suite?.digest_matches !== false;
        const integrity = suite?.integrity;
        return (
          <div
            key={String(suiteId)}
            data-testid={`evaluation-suite-${suiteId}`}
            data-integrity={typeof integrity === "string" ? integrity : "unknown"}
            data-digest-matches={
              suite?.digest_matches === true
                ? "true"
                : suite?.digest_matches === false
                  ? "false"
                  : "unknown"
            }
            className={`rounded-lg border px-3 py-2 space-y-1 ${
              digestMismatch ? "border-red-200 bg-red-50/40" : "border-gray-100 bg-gray-50/60"
            }`}
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-xs text-gray-800" dir="ltr">
                {textOrNotReported(suiteId)}
              </span>
              <Chip>גרסה {textOrNotReported(suite?.suite_version)}</Chip>
              <Chip>{countText(suite?.case_count)} מקרים</Chip>
              <Chip
                tone={
                  integrity === "ok"
                    ? "bg-green-50 text-green-700 border-green-200"
                    : "bg-amber-50 text-amber-800 border-amber-200"
                }
              >
                שלמות: {integrityLabel(integrity)}
              </Chip>
              {suite?.digest_matches === true && (
                <Chip tone="bg-green-50 text-green-700 border-green-200">דיגסט תואם</Chip>
              )}
              {digestUnknown && <Chip>דיגסט: {EVALUATION_NOT_REPORTED_HE}</Chip>}
            </div>
            {digestMismatch && (
              <p role="alert" className="text-xs text-red-700">
                הדאטהסט שונה מהדיגסט המוצהר — אין להסתמך על תוצאות שנמדדו מולו עד לתיקון או לעליית
                גרסה חדשה.
              </p>
            )}
            {Array.isArray(suite?.integrity_problems) && suite.integrity_problems.length > 0 && (
              <ul className="text-[11px] text-amber-800 list-disc list-inside">
                {suite.integrity_problems.map((problem, index) => (
                  <li key={index}>{textOrNotReported(problem)}</li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Evidence: the durable, digest-verified evaluation records ────────────────

function EvidenceCard({ row }) {
  const record = row?.record;
  if (!record || typeof record !== "object") {
    return (
      <p
        role="alert"
        data-testid={`evaluation-evidence-${row?.evidence_id || "unknown"}`}
        className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
      >
        רשומת ראיה בלי גוף רשומה — לא דווח על מה נמדד.
      </p>
    );
  }

  const scores = record.scores && typeof record.scores === "object" ? record.scores : null;
  const promotion = row.promotion && typeof row.promotion === "object" ? row.promotion : null;
  const reasons = Array.isArray(record.arm_reasons) ? record.arm_reasons : null;
  const arm = record.recommended_arm;
  const gate = record.gate;

  return (
    <div
      data-testid={`evaluation-evidence-${row.evidence_id}`}
      data-arm={typeof arm === "string" ? arm : "unknown"}
      data-gate={typeof gate === "string" ? gate : "unknown"}
      data-promoted={!promotion ? "unknown" : promotion.promoted === true ? "true" : "false"}
      className="rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2 space-y-1.5"
    >
      <div className="flex flex-wrap items-center gap-2">
        <Chip
          tone={
            arm === "ADOPT"
              ? "bg-green-50 text-green-700 border-green-200"
              : arm === "WATCH"
                ? "bg-amber-50 text-amber-800 border-amber-200"
                : "bg-red-50 text-red-600 border-red-200"
          }
        >
          {armLabel(arm)}
        </Chip>
        <Chip>שער: {gateLabel(gate)}</Chip>
        <span className="font-mono text-xs text-gray-800" dir="ltr">
          {textOrNotReported(record.candidate_ref)}
        </span>
        <span className="text-[11px] text-gray-400">
          {textOrNotReported(record.candidate_id)}
        </span>
      </div>

      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-600">
        <span dir="ltr">{textOrNotReported(record.suite_id)}</span>
        <span>גרסה {textOrNotReported(record.suite_version)}</span>
        <span>נמדד: {dateText(row.recorded_at || record.recorded_on)}</span>
        <span>כשירות: {eligibilityLabel(record.promotion_eligibility)}</span>
      </div>

      {scores ? (
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-600">
          <span>איכות: {percentText(scores.quality_rate)}</span>
          <span>בטיחות: {percentText(scores.safety_pass_rate)}</span>
          <span>
            מקרים: {countText(scores.cases_passed)} / {countText(scores.cases_total)}
          </span>
          <span>עלות: {typeof scores.total_cost_usd === "number" ? `$${scores.total_cost_usd.toFixed(2)}` : EVALUATION_NOT_REPORTED_HE}</span>
        </div>
      ) : (
        <p className="text-[11px] text-gray-400">הציונים לא דווחו ברשומה.</p>
      )}

      {reasons && reasons.length > 0 && (
        <p className="text-[11px] text-gray-500">
          נימוקים: {reasons.map((reason) => armReasonLabel(reason)).join(" · ")}
        </p>
      )}

      <p
        data-testid={`evaluation-evidence-promotion-${row.evidence_id}`}
        className={`text-[11px] ${promotion?.promoted ? "text-green-700" : "text-gray-500"}`}
      >
        {!promotion
          ? "מצב הקידום לא דווח."
          : promotion.promoted
            ? `קודם על ידי ${textOrNotReported(promotion.promoted_by)} ב־${dateText(promotion.recorded_on)}`
            : "לא קודם על ידי אדם."}
      </p>
    </div>
  );
}

export function EvaluationEvidenceSection({ evidence }) {
  const records = evidence?.records;
  if (!Array.isArray(records)) {
    return (
      <p
        role="alert"
        data-testid="evaluation-evidence-not-reported"
        className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
      >
        המקור לא דיווח על ראיות ההערכה (records) — לא ידוע, ולא אפס.
      </p>
    );
  }

  if (records.length === 0) {
    return (
      <p data-testid="evaluation-evidence-empty" className="text-sm text-gray-500">
        אין ראיות הערכה שמורות. מסלול ההקלטה והקידום אינו נפתח מהמסך הזה.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {records.map((row) => (
        <EvidenceCard key={row?.evidence_id || `${row?.record?.evidence_key || "row"}`} row={row} />
      ))}
      {evidence?.truncated === true && (
        <p data-testid="evaluation-evidence-truncated" className="text-[11px] text-amber-800">
          מוצגות {records.length} ראיות מתוך {countText(evidence.inspected_rows)} שנקראו — הרשימה
          מקוצרת בתקרת המקור.
        </p>
      )}
    </div>
  );
}

// ─── Recorded human promotions ────────────────────────────────────────────────

function promotionAuthoritySentence(decision) {
  const unchanged =
    decision?.production_default_changed === false &&
    decision?.live_routing_changed === false &&
    decision?.authority_granted === false;
  if (unchanged && decision?.requires_separate_production_rollout === true) {
    return "אין שינוי בברירת מחדל, בניתוב או בהרשאה; נדרש שחרור פרודקשן נפרד.";
  }
  return "הרשומה אינה מצהירה במלואה על היעדר שינוי — יש לבדוק ידנית לפני הסתמכות.";
}

export function EvaluationPromotionsSection({ promotions }) {
  const rows = promotions?.promotions;
  if (!Array.isArray(rows)) {
    return (
      <p
        role="alert"
        data-testid="evaluation-promotions-not-reported"
        className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
      >
        המקור לא דיווח על החלטות הקידום (promotions) — לא ידוע, ולא אפס.
      </p>
    );
  }

  if (rows.length === 0) {
    return (
      <p data-testid="evaluation-promotions-empty" className="text-sm text-gray-500">
        אין החלטות קידום אנושיות רשומות.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {rows.map((decision) => (
        <div
          key={decision?.decision_id}
          data-testid={`evaluation-promotion-${decision?.decision_id}`}
          data-arm={typeof decision?.recommended_arm === "string" ? decision.recommended_arm : "unknown"}
          className="rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2 space-y-1"
        >
          <div className="flex flex-wrap items-center gap-2">
            <Chip tone="bg-green-50 text-green-700 border-green-200">
              {armLabel(decision?.recommended_arm)}
            </Chip>
            <span className="font-mono text-xs text-gray-800" dir="ltr">
              {textOrNotReported(decision?.candidate_ref)}
            </span>
            <span dir="ltr" className="text-[11px] text-gray-500">
              {textOrNotReported(decision?.suite_id)}
            </span>
          </div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-600">
            <span>קידם: {textOrNotReported(decision?.promoted_by)}</span>
            <span>נרשם: {dateText(decision?.recorded_on)}</span>
            <span>היקף: {textOrNotReported(decision?.decision_scope)}</span>
          </div>
          <p className="text-[11px] text-gray-500">{promotionAuthoritySentence(decision)}</p>
        </div>
      ))}
    </div>
  );
}

// ─── The declared promotion contract ──────────────────────────────────────────

export function EvaluationContractSection({ promotionContract }) {
  if (!promotionContract || typeof promotionContract !== "object") {
    return null;
  }
  const arms = Array.isArray(promotionContract.arms) ? promotionContract.arms : null;
  const gates = Array.isArray(promotionContract.gates) ? promotionContract.gates : null;

  return (
    <div className="space-y-2 text-[11px] text-gray-600">
      <div className="flex flex-wrap gap-2">
        <Chip>זרועות: {arms ? arms.map((arm) => armLabel(arm)).join(" · ") : EVALUATION_NOT_REPORTED_HE}</Chip>
        <Chip>שערים: {gates ? gates.map((gate) => gateLabel(gate)).join(" · ") : EVALUATION_NOT_REPORTED_HE}</Chip>
      </div>
      <p>
        קידום דורש: {textOrNotReported(promotionContract.promotion_requires)}
      </p>
      <p className="text-gray-400">
        הקידום הוא ראיה מבוקרת; הוא אינו משנה ברירת מחדל, ניתוב או הרשאה, והשחרור לפרודקשן נדרש
        בנפרד.
      </p>
    </div>
  );
}

// ─── Authority: what the sources state they do and do not do ──────────────────

export function EvaluationAuthoritySection({ contract, evidence }) {
  // Each flag has a safe polarity that is not the same for all of them: writing product state,
  // changing a production default or changing live routing is safe only when false, while
  // `advisory_only` is safe only when true. Treating "any true flag" as unsafe would red the
  // read-only path precisely because it declared itself advisory.
  const flags = [
    ["כתיבת מצב מוצר", evidence?.writes_product_state, true],
    ["שינוי ברירת מחדל בפרודקשן", evidence?.changes_production_default, true],
    ["שינוי ניתוב חי", evidence?.changes_live_routing, true],
    ["ייעוץ בלבד", contract?.advisory_only, false],
  ];
  const states = flags.map(([, value]) => flagState(value));
  const complete = states.every((state) => state !== "unknown");
  const unsafe = flags.some(([, value, unsafeWhen]) => value === unsafeWhen);
  const unknownLabel = EVALUATION_NOT_REPORTED_HE;

  return (
    <div
      data-testid="evaluation-authority"
      data-authority-complete={complete ? "true" : "false"}
      data-authority-unsafe={unsafe ? "true" : "false"}
      className={`rounded-lg border px-3 py-2 space-y-1 text-[11px] ${
        unsafe
          ? "border-red-200 bg-red-50 text-red-700"
          : complete
            ? "border-green-200 bg-green-50 text-green-800"
            : "border-amber-200 bg-amber-50 text-amber-800"
      }`}
    >
      {flags.map(([label, value]) => (
        <div key={label} className="flex flex-wrap gap-x-2">
          <span className="font-medium">{label}:</span>
          <span>{value === undefined ? unknownLabel : flagLabel(value)}</span>
        </div>
      ))}
      <p>
        {unsafe
          ? "השרת מדווח על סמכות שאינה בטוחה — אין להסתמך על המסך כמסלול קריאה בלבד."
          : complete
            ? "כל הדגלים שהשרת דיווח הם false או ייעוץ בלבד: המסך אינו משנה מוצר, ברירת מחדל או ניתוב."
            : "לא כל דגלי הסמכות דווחו; היעדר דיווח אינו הצהרת false."}
      </p>
    </div>
  );
}

// ─── The workspace ────────────────────────────────────────────────────────────

export default function AgentEvaluationWorkspacePanel({
  contract,
  promotionContract,
  evidence,
  promotions,
  states = {},
}) {
  const stateOf = (key) => {
    if (states && states[key]) return states[key];
    return SOURCE_READY;
  };

  return (
    <div className="space-y-3" data-testid="evaluation-workspace">
      <SectionShell
        title="מערכי הערכה וגרסאות"
        description="הגרסה והדיגסט שכל מערך מצהיר עליהם מול הדיגסט שהמקרים מייצרים עכשיו. אי־התאמה היא ראיה שגרסה לא עלתה."
        testId="evaluation-suites-section"
      >
        <SourceGate
          state={stateOf("contract")}
          payload={contract}
          testId="evaluation-suites"
          label="מערכי ההערכה"
          notReportedTestId="evaluation-suites-not-reported"
        >
          {(payload) => <EvaluationSuitesSection contract={payload} />}
        </SourceGate>
      </SectionShell>

      <SectionShell
        title="ראיות הערכה שמורות"
        description="רשומות שנמדדו והופקדו בידי שני אדם ומכונה; כל רשומה נושאת את הזרוע, השער, הנימוקים והדיגסט שלה."
        testId="evaluation-evidence-section"
      >
        <SourceGate
          state={stateOf("evidence")}
          payload={evidence}
          testId="evaluation-evidence"
          label="ראיות ההערכה"
          notReportedTestId="evaluation-evidence-not-reported"
        >
          {(payload) => <EvaluationEvidenceSection evidence={payload} />}
        </SourceGate>
      </SectionShell>

      <SectionShell
        title="החלטות קידום אנושיות"
        description="קידום הוא החלטה אנושית מפורשת ונרשמת; הוא אינו פריסה ואינו שינוי ניתוב."
        testId="evaluation-promotions-section"
      >
        <SourceGate
          state={stateOf("promotions")}
          payload={promotions}
          testId="evaluation-promotions"
          label="החלטות הקידום"
          notReportedTestId="evaluation-promotions-not-reported"
        >
          {(payload) => <EvaluationPromotionsSection promotions={payload} />}
        </SourceGate>
      </SectionShell>

      <SectionShell
        title="חוזה הקידום"
        description="הזרועות, השערים ותנאי הקידום שהשרת מצהיר עליהם."
        testId="evaluation-contract-section"
      >
        <SourceGate
          state={stateOf("promotionContract")}
          payload={promotionContract}
          testId="evaluation-contract"
          label="חוזה הקידום"
          notReportedTestId="evaluation-contract-not-reported"
        >
          {(payload) => <EvaluationContractSection promotionContract={payload} />}
        </SourceGate>
      </SectionShell>

      <SectionShell
        title="סמכות המסלול"
        description="הדגלים שהשרת מדווח עליהם בעצמו — המסך מציג אותם ואינו מניח אותם."
        testId="evaluation-authority-section"
      >
        <EvaluationAuthoritySection contract={contract} evidence={evidence} />
      </SectionShell>
    </div>
  );
}

export { SOURCE_FAILED, SOURCE_LOADING, SOURCE_READY };
